/**
 * OKLCH color math (Björn Ottosson's OKLab, https://bottosson.github.io/posts/oklab/),
 * self-contained rather than pulled in as a dependency — this is the entire
 * surface the token derivation needs: hex in, perceptually-uniform lightness
 * steps out, hex back. Gamut clipping is a simple channel clamp rather than
 * full gamut mapping; good enough for a brand color plus derived tints, not
 * meant for general-purpose color work.
 */
export interface Oklch {
  l: number; // 0..1 perceptual lightness
  c: number; // chroma, typically 0..~0.4
  h: number; // hue in degrees, 0..360
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function srgbDecode(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function srgbEncode(c: number): number {
  const clamped = Math.min(1, Math.max(0, c));
  return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
}

export function hexToRgb(hex: string): Rgb {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex);
  if (!match?.[1]) throw new Error(`invalid hex color: "${hex}"`);
  const int = Number.parseInt(match[1], 16);
  return { r: ((int >> 16) & 255) / 255, g: ((int >> 8) & 255) / 255, b: (int & 255) / 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const toByte = (c: number) => Math.round(Math.min(1, Math.max(0, c)) * 255);
  return `#${[r, g, b].map((c) => toByte(c).toString(16).padStart(2, "0")).join("")}`;
}

function linearRgbToOklab({ r, g, b }: Rgb): { L: number; a: number; b: number } {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

function oklabToLinearRgb({ L, a, b }: { L: number; a: number; b: number }): Rgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

export function hexToOklch(hex: string): Oklch {
  const srgb = hexToRgb(hex);
  const linear: Rgb = { r: srgbDecode(srgb.r), g: srgbDecode(srgb.g), b: srgbDecode(srgb.b) };
  const lab = linearRgbToOklab(linear);
  const c = Math.hypot(lab.a, lab.b);
  const h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  return { l: lab.L, c, h: h < 0 ? h + 360 : h };
}

export function oklchToHex({ l, c, h }: Oklch): string {
  const hRad = (h * Math.PI) / 180;
  const lab = { L: l, a: c * Math.cos(hRad), b: c * Math.sin(hRad) };
  const linear = oklabToLinearRgb(lab);
  return rgbToHex({ r: srgbEncode(linear.r), g: srgbEncode(linear.g), b: srgbEncode(linear.b) });
}

/** Relative luminance per WCAG 2.x, from sRGB hex — the input to contrast ratio, not OKLab's `L`. */
export function relativeLuminance(hex: string): number {
  const srgb = hexToRgb(hex);
  const r = srgbDecode(srgb.r);
  const g = srgbDecode(srgb.g);
  const b = srgbDecode(srgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colors, order-independent, range [1, 21]. */
export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}
